/**
 * Fetches the anonymous public-survey M2M token from the SWA built-in API.
 *
 * GET /api/public-token is served by api/public_token/__init__.py — a Python Azure Function
 * deployed alongside the Static Web App on the same domain. It is publicly accessible
 * (no VPN, no APIM JWT) and handles Redis caching server-side so all concurrent respondents
 * share a single Okta token, avoiding 429 rate-limit errors.
 *
 * The browser caches the token in sessionStorage until 60 s before expiry so repeated
 * calls within one browser session (survey load + submit) skip the network entirely.
 */

const CACHE_KEY = 'survey_public_m2m';
const REFRESH_BUFFER_S = 60;

/** Deduplicates concurrent fetch calls during initial page load. */
let _inflight = null;

/**
 * Returns a valid M2M bearer token.
 * Returns null if the SWA API is unreachable (local dev without SWA CLI).
 */
export async function getPublicM2MToken() {
  const cached = _readCache();
  if (cached) return cached;

  if (_inflight) return _inflight;
  _inflight = _fetch().finally(() => {
    _inflight = null;
  });
  return _inflight;
}

function _readCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { token, expiresAt } = JSON.parse(raw);
    if (Date.now() / 1000 < expiresAt) return token;
    sessionStorage.removeItem(CACHE_KEY);
  } catch {
    // corrupt entry — ignore
  }
  return null;
}

async function _fetch() {
  try {
    const resp = await fetch('/api/public-token');
    if (!resp.ok) return null; // 404 in local dev — caller treats null as "no auth needed"
    const data = await resp.json();
    const token = data?.access_token;
    if (!token) return null;

    // Server handles real expiry via Redis TTL. Browser uses a conservative 1-hr window.
    const expiresAt = Math.floor(Date.now() / 1000) + 3600 - REFRESH_BUFFER_S;
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ token, expiresAt }));
    return token;
  } catch {
    return null;
  }
}
